import { FC, Fragment, useMemo, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import caresLogoWide from '../../assets/images/cares/logo-wide.png';
import google from '../../assets/images/media/apps/google.png';
import authCover from '../../assets/images/cares/auth-cover.png';
import { getCaresShellBootstrap } from './shell/bootstrap';
import './cares-authentication.scss';

const CaresLogin: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const csrfToken = useMemo(() => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '', []);
  const bootstrap = getCaresShellBootstrap();
  const googleSsoConfigured = bootstrap.authProviders.google.configured;
  const validationMessages = Object.values(bootstrap.validationErrors ?? {}).flat();
  const emailError = bootstrap.validationErrors?.email?.[0];
  const passwordError = bootstrap.validationErrors?.password?.[0];
  const emailErrorId = emailError ? 'cares-login-email-error' : undefined;
  const passwordErrorId = passwordError ? 'cares-login-password-error' : undefined;
  const [email, setEmail] = useState(() => bootstrap.authPage.email ?? '');
  const [remember, setRemember] = useState(() => bootstrap.authPage.remember);

  return (
    <Fragment>
      <Row className="authentication cares-authentication mx-0 bg-white">
        <Col xxl={5} xl={5} lg={12}>
          <Row className="justify-content-center align-items-center h-100">
            <Col xxl={6} xl={9} lg={6} md={6} sm={8} className="col-12">
              <Card className="custom-card shadow-none my-4">
                <Card.Body className="p-4">
                  <div className="mb-3 d-flex justify-content-center">
                    <Link to="/">
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-logo" />
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-dark" />
                    </Link>
                  </div>
                  <p className="h5 mb-2 text-center">Sign in to CARES</p>
                  <p className="mb-4 text-muted op-7 fw-normal text-center">
                    Use your CARES account to access your schedule and the tools available to your role.
                  </p>

                  {bootstrap.status ? <div className="alert alert-success" role="status">{bootstrap.status}</div> : null}
                  {validationMessages.length > 0 ? (
                    <div className="alert alert-danger" role="alert">
                      {validationMessages.map((message, index) => (
                        <div key={`${message}-${index}`}>{message}</div>
                      ))}
                    </div>
                  ) : null}

                  <Form method="post" action="/login">
                    <input type="hidden" name="_token" value={csrfToken} />
                    <input type="hidden" name="remember_present" value="1" />
                    <Row className="gy-3">
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-login-email" className="text-default">
                          Email
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          size="lg"
                          id="cares-login-email"
                          placeholder="you@example.com"
                          autoComplete="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          isInvalid={Boolean(emailError)}
                          aria-describedby={emailErrorId}
                          required
                        />
                        {emailError ? <Form.Control.Feedback id={emailErrorId} type="invalid" className="d-block">{emailError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={12} className="mb-2">
                        <Form.Label htmlFor="cares-login-password" className="text-default d-block">
                          Password
                          <a href="/forgot-password" className="float-end text-danger">
                            Forgot password?
                          </a>
                        </Form.Label>
                        <div className="position-relative authentication-input">
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            size="lg"
                            id="cares-login-password"
                            placeholder="password"
                            autoComplete="current-password"
                            isInvalid={Boolean(passwordError)}
                            aria-describedby={passwordErrorId}
                            required
                          />
                          <button
                            type="button"
                            className="show-password-button text-muted bg-transparent border-0"
                            onClick={() => setShowPassword((value) => !value)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <i className="ri-eye-line align-middle"></i>
                            ) : (
                              <i className="ri-eye-off-line align-middle"></i>
                            )}
                          </button>
                        </div>
                        {passwordError ? <Form.Control.Feedback id={passwordErrorId} type="invalid" className="d-block">{passwordError}</Form.Control.Feedback> : null}
                        <div className="mt-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value="1"
                              id="caresLoginRemember"
                              name="remember"
                              checked={remember}
                              onChange={(event) => setRemember(event.target.checked)}
                            />
                            <label className="form-check-label text-muted fw-normal" htmlFor="caresLoginRemember">
                              Remember me
                            </label>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="d-grid mt-4">
                      <button type="submit" className="btn btn-lg btn-primary">
                        Sign In
                      </button>
                    </div>
                  </Form>

                  <div className="text-center my-3 authentication-barrier">
                    <span>OR</span>
                  </div>
                  {googleSsoConfigured ? (
                    <div className="d-grid mb-3">
                      <a href="/sso/google/redirect" className="btn btn-lg border d-flex align-items-center justify-content-center">
                        <span className="avatar avatar-xs">
                          <img src={google} alt="" />
                        </span>
                        <span className="lh-1 ms-2 fs-13 text-default">Continue with Google</span>
                      </a>
                    </div>
                  ) : (
                    <div className="d-grid mb-3">
                      <div className="btn btn-lg border d-flex align-items-center justify-content-center disabled" aria-disabled="true">
                        <span className="avatar avatar-xs">
                          <img src={google} alt="" />
                        </span>
                        <span className="lh-1 ms-2 fs-13 text-muted">Google Sign-In not configured</span>
                      </div>
                    </div>
                  )}
                  <p className="text-muted small text-center mb-0">
                    {googleSsoConfigured
                      ? 'First-time Google SSO users must sign in with a local CARES account and complete explicit account linking.'
                      : 'Google sign-in stays disabled until the client ID and callback are configured.'}
                  </p>
                  <div className="text-center">
                    <p className="text-muted mt-3 mb-0">
                      Need an account?{' '}
                      <a href="/signup" className="text-primary">
                        Sign Up
                      </a>
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xxl={7} xl={7} lg={7} className="d-xl-block d-none px-0">
          <div className="authentication-cover bg-primary">
            <div>
              <div className="authentication-cover-image">
                <img src={authCover} alt="CARES clinical scheduling workspace" />
              </div>
              <div className="text-center mb-4">
                <h1 className="text-fixed-white">CARES Login</h1>
                <p className="text-fixed-white op-8 mb-0">Clinical Automated Rostering & Efficient Scheduling</p>
              </div>
              <div className="btn-list text-center">
                <Link to="/" className="btn btn-icon authentication-cover-icon btn-wave">
                  <i className="ri-home-4-line fw-bold"></i>
                </Link>
                <a href="/signup" className="btn btn-icon authentication-cover-icon btn-wave">
                  <i className="ri-user-add-line fw-bold"></i>
                </a>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default CaresLogin;
